import EventEmitter from '../common/EventEmitter';
import logger from '../log';
import {
  ApiEvents,
  ConnectionStates,
  ConnectionEventTypes,
  NodeIdInfo,
  ConnectionChangeState,
  ConnectionStatusEvent
} from '@kollective-frontend/ksdk-api';
import { UNKNOWN_CONNECTION_FAILURE } from '../api/diagnostics/constants';
import { DeprecatedEvents } from '../public/events';
const log = logger.getLogger(__filename);

export interface Caller {
  connectionEstablishedEventCompleted?: boolean;
}

export interface ConnectionChange {
  caller?: Caller;
  closeEvent?: CloseEvent;
  error?: Record<string, unknown>;
  hasSucceeded?: boolean;
  message?: string;
  node?: NodeIdInfo;
  state: ConnectionChangeState;
  type: string;
}

export const setMessage = (
  data: ConnectionEventTypes | ConnectionStatusEvent,
  obj: ConnectionChange
): void => {
  const messages: string[] = [];
  if (obj.message) {
    messages.push(`message: ${obj.message}`);
  }
  if (obj.error && typeof obj.error.message === 'string') {
    messages.push(`Error message: ${obj.error.message}`);
  }
  if (obj.closeEvent && obj.closeEvent.reason) {
    messages.push(`Close Reason: ${obj.closeEvent.reason}`);
  }
  if (obj.closeEvent && obj.closeEvent.code) {
    messages.push(`error code: ${obj.closeEvent.code}`);
  }
  if (messages.length > 0) {
    data.message = messages.join(', ');
  } else if ((data as ConnectionEventTypes).status == 'failed') {
    Object.entries(obj.node || {}).forEach(([key, value]) => {
      messages.push(`${key}: ${value}`);
    });
    Object.entries(obj.error || {}).forEach(([key, value]) => {
      messages.push(`${key}: ${value}`);
    });
    const msg = messages.length > 0 ? ` (${messages.join(', ')})` : '';
    data.message = `${UNKNOWN_CONNECTION_FAILURE}${msg}`;
  }
};

const emitConnectionChanged = (obj?: ConnectionChange): boolean => {
  if (!obj) {
    return false;
  }
  const emitter = EventEmitter.getInstance();
  // On first call for caller, just confirm whether or not the connection has
  // succeeded. The pattern is: first call: WEBSOCKET_CONNECT, all future calls
  // CONNECTION_STATUS_EVENT.
  if (obj.caller && !obj.caller.connectionEstablishedEventCompleted) {
    obj.caller.connectionEstablishedEventCompleted = true;
    const status: ConnectionStates = obj.hasSucceeded ? 'success' : 'failed';
    const data: ConnectionEventTypes = { status, type: obj.type };
    emitter.emit(DeprecatedEvents.WEBSOCKET_CONNECTION, data);
    setMessage(data, obj);
    emitter.emit(ApiEvents.WEBSOCKET_CONNECT, { data });
    return true;
  } else {
    // If we get here, we either are not dealing with the result of an initial
    // connection attempt. Or we are dealing with an event where the webrtc peer
    // has changed.

    // If we get here, it means that the socket was successfully established.
    // This is at least the second call for this websocket, so we want to send
    // a connection changed event, rather than a connect success/failure event.
    const { state, type } = obj;
    const data: ConnectionStatusEvent = { state, type };
    setMessage(data, obj);
    log.debug('ConnectionStatusEvent:', obj);

    if (obj.closeEvent) {
      if (obj.closeEvent.code) {
        data.code = obj.closeEvent.code;
      }
      if (typeof obj.closeEvent.wasClean !== 'undefined') {
        data.wasClean = obj.closeEvent.wasClean;
      }
    }
    if (obj.node) {
      data.node = obj.node;
    }
    emitter.dispatch({ type: ApiEvents.CONNECTION_STATUS_EVENT, data });
  }
  return false;
};

export default emitConnectionChanged;
